import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { createUseStyles } from 'react-jss';

import { Qmr } from '@packages/models/api';
import { investigationService } from '@web/services/singletons';
import { Icon, LoadingCircle, TextBadge, Typography } from '@packages/ui/shared';
import colors from '@packages/core/styles/colors';

const INLINE_LIMIT = 5;

interface InvestigationQmrsExpansionProps {
    investigationId: string;
    columnCount: number;
}

const useStyles = createUseStyles({
    wrapper: {
        backgroundColor: colors.grayOne,
        padding: '16px 24px',
    },
    stateContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: 24,
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayThree}`,
        '& th': {
            textAlign: 'left',
            fontSize: 12,
            fontWeight: 600,
            color: colors.textDarkSecondary,
            padding: '8px 12px',
            borderBottom: `1px solid ${colors.grayThree}`,
            whiteSpace: 'nowrap',
        },
        '& td': {
            fontSize: 13,
            padding: '8px 12px',
            color: colors.textDarkPrimary,
            borderBottom: `1px solid ${colors.grayTwo}`,
            verticalAlign: 'middle',
        },
        '& tbody tr:last-child td': {
            borderBottom: 0,
        },
    },
    qmrLink: {
        color: colors.blueLink,
        fontWeight: 500,
        textDecoration: 'none',
        '&:hover': {
            color: colors.blueLinkHover,
            textDecoration: 'underline',
        },
    },
    viewAll: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        marginTop: 12,
        color: colors.blueLink,
        fontWeight: 600,
        fontSize: 13,
        textDecoration: 'none',
        '&:hover': {
            color: colors.blueLinkHover,
            textDecoration: 'underline',
        },
    },
});

export const InvestigationQmrsExpansion: FC<InvestigationQmrsExpansionProps> = ({ investigationId, columnCount }) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [qmrs, setQmrs] = useState<Qmr[]>([]);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(false);

        investigationService
            .getQmrsFromInvestigationById({
                investigationId,
                searchParams: `page=0&size=${INLINE_LIMIT}`,
                ignoreCache: true,
            })
            .then((response) => {
                if (!active) {
                    return;
                }
                if (!response.success) {
                    if (response.aborted) {
                        return;
                    }
                    throw response.data;
                }
                setQmrs(response.data.qmrs || []);
                setTotalCount(response.data.totalCount || 0);
            })
            .catch(() => {
                if (active) {
                    setError(true);
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [investigationId]);

    const renderBody = () => {
        if (loading) {
            return (
                <div className={classes.stateContainer}>
                    <LoadingCircle size={24} />
                </div>
            );
        }

        if (error) {
            return (
                <div className={classes.stateContainer}>
                    <Icon name="warning" color="redOne" size={18} />
                    <Typography variant="caption" color="redOne">
                        {t(
                            'investigations:expandedRow.error',
                            'Unable to load QMRs for this investigation. Please try again.'
                        )}
                    </Typography>
                </div>
            );
        }

        if (qmrs.length === 0) {
            return (
                <div className={classes.stateContainer}>
                    <Typography variant="caption" color="graySix">
                        {t('investigations:expandedRow.empty', 'No QMRs are associated with this investigation.')}
                    </Typography>
                </div>
            );
        }

        return (
            <>
                <table className={classes.table}>
                    <thead>
                        <tr>
                            <th>{t('investigations:expandedRow.columns.qmrId', 'QMR ID')}</th>
                            <th>{t('investigations:expandedRow.columns.vin', 'VIN')}</th>
                            <th>{t('investigations:expandedRow.columns.year', 'Year')}</th>
                            <th>{t('investigations:expandedRow.columns.carline', 'Carline')}</th>
                            <th>{t('investigations:expandedRow.columns.failCode', 'Fail Code')}</th>
                            <th>{t('investigations:expandedRow.columns.dtc', 'DTC')}</th>
                            <th>{t('investigations:expandedRow.columns.submitted', 'Submitted')}</th>
                            <th>{t('investigations:expandedRow.columns.status', 'Status')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {qmrs.map((qmr) => (
                            <tr key={qmr.qmrId}>
                                <td>
                                    <a
                                        className={classes.qmrLink}
                                        href={`/qmrs/${qmr.displayIdentifier}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {qmr.displayIdentifier}
                                    </a>
                                </td>
                                <td style={{ fontFamily: 'monospace' }}>{qmr.vin || '—'}</td>
                                <td>{qmr.modelYear || '—'}</td>
                                <td>{qmr.carlineName || '—'}</td>
                                <td>{qmr.failCode || '—'}</td>
                                <td>
                                    {qmr.dtcCodes && qmr.dtcCodes.length > 0
                                        ? qmr.dtcCodes.map((code) => code.dtcCode).join(', ')
                                        : '—'}
                                </td>
                                <td>
                                    {qmr.firstSubmittedTimestampDescription || qmr.createdTimestampDescription || '—'}
                                </td>
                                <td>
                                    {qmr.qmrStatus?.description ? (
                                        <div className="d-flex align-items-center">
                                            <TextBadge variant="green">{qmr.qmrStatus.description}</TextBadge>
                                        </div>
                                    ) : (
                                        '—'
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {totalCount > qmrs.length && (
                    <Link className={classes.viewAll} to={`/investigations/${investigationId}`}>
                        {t('investigations:expandedRow.viewAll', 'View all {{count}} QMRs', { count: totalCount })}
                        <Icon name="chevron-right" color="blueLink" size={14} />
                    </Link>
                )}
            </>
        );
    };

    return (
        <td colSpan={columnCount}>
            <div className={classes.wrapper}>{renderBody()}</div>
        </td>
    );
};
